import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { isEqual, toNumber, isEmpty } from "@util/utils";
import { parseDate } from "@util/moment";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { PageLayout } from "@icoach/components";
import SmartFitnessBandContractApply from "@icoach/smartFitnessBand/contract/SmartFitnessBandContractApply";
import SmartFitnessBandContractDetail from "@icoach/smartFitnessBand/contract/SmartFitnessBandContractDetail";
import { SmartFitnessProvider } from "@icoach/smartFitnessBand/components/SmartFitnessContext";
import { initMemberState, initMainSectionState } from "@icoach/smartFitnessBand/schema/SmartFitnessBandSchema";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import SmartFitnessBandDialogBox from "@icoach/smartFitnessBand/dialog/SmartFitnessBandDialogBox";
import useDeviceOrderApi from "@apis/useDeviceOrderApi";
import MemberLineBindingIconButton from "../../memberDetail/components/MemberLineBindingIconButton";

const SmartFitnessBandContractPage = props => {
	const { getDeviceOrderApplyOptionsApi } = useDeviceOrderApi();
	const [sourceData, setSourceData] = useState(null);
	const [options, setOptions] = useState({ employeeOptions: [], checkoutPaymentTypeOptions: [], deviceTimeTypeOptions: [] });
	/** @type {{ page: string, contractID: string }} */
	const { page, contractID: _contractID } = useParams();
	const contractID = toNumber(_contractID);
	const isReadOnly = isEqual(page, "readonly-contract");
	const dialogRef = React.useRef(null);

	useInactivityTimerToggle();

	const handleApplySourceData = (targetData, isPartialUpdate = false) => {
		if (isEmpty(targetData))
			setSourceData({
				memberInfo: { ...initMemberState },
				mainSection: { ...initMainSectionState },
				invoice: {},
			});
		else if (isPartialUpdate) {
			setSourceData(prevData => {
				if (!prevData) return prevData;

				// 只更新 getRangeFeeApi 的資料
				const updatedMainSection = { ...prevData.mainSection };

				// 處理 plan 物件的更新
				if (targetData.planID !== 0 || targetData.planType !== 0) {
					updatedMainSection.plan = {
						...prevData.mainSection.plan,
						...(targetData.planID !== 0 && { planID: targetData.planID }),
						...(targetData.planType !== 0 && { planType: targetData.planType }),
					};
				}

				// 更新其他欄位（除 planID 和 planType）
				Object.keys(targetData).forEach(key => {
					if (key !== "planID" && key !== "planType") {
						updatedMainSection[key] = targetData[key];
					}
				});

				return {
					contractVersion: prevData.contractVersion,
					applyDate: prevData.applyDate,
					memberInfo: prevData.memberInfo,
					mainSection: updatedMainSection,
					invoice: prevData.invoice,
					payment: prevData.payment,
					notes: prevData.notes,
				};
			});
		} else {
			const {
				address = "",
				cellPhone = "",
				identifierID = 0,
				mail = "",
				memberNo = "",
				memberID = 0,
				monthFee = 0,
				firstMonthFee = 0,
				trainingFee = 0,
				reviewStartDate = null,
				reviewEndDate = null,
				invoice,
				payment,
				applyDate,
				contractVersion,
				debitFirstDate,
				subscriptionStartDate,
				discountPlanOptions = [],
				canUseDevice = false,
				canUseDeviceText = "",
				notes = null,
				plan = { planID: 0, planType: 0 },
			} = targetData;
			setSourceData({
				contractVersion: contractVersion,
				applyDate: parseDate(applyDate),
				memberInfo: {
					memberID: memberID,
					memberPhone: cellPhone,
					memberEmail: mail,
					memberAddress: address,
					memberNo: memberNo,
					memberIdentityNumber: identifierID,
					canUseDevice: canUseDevice,
					canUseDeviceText: canUseDeviceText,
				},
				mainSection: {
					monthFee: monthFee,
					reviewStartDate: reviewStartDate,
					reviewEndDate: reviewEndDate,
					discountPlanOptions: [...discountPlanOptions],
					subscriptionStartDate: subscriptionStartDate,
					firstMonthFee: firstMonthFee,
					trainingFee: trainingFee,
					debitFirstDate: debitFirstDate,
					plan: { ...plan },
				},
				invoice: { ...invoice, mail, cellPhone },
				payment: { ...payment },
				notes: notes,
			});
		}
	};

	const handleDetailSourceData = targetData => {
		setSourceData(targetData);
	};

	const SmartFitnessValue = {
		deviceOrderID: contractID,
		contractApplySourceData: sourceData, // 申請書專用
		contractDetailSourceData: sourceData, // 明細專用
		options,
		setApplySourceData: (targetData, isPartialUpdate) => handleApplySourceData(targetData, isPartialUpdate),
		setDetailSourceData: targetData => handleDetailSourceData(targetData),
	};

	const initOptions = async () => {
		const resp = await getDeviceOrderApplyOptionsApi();
		if (resp) setOptions(resp);
	};

	useEffect(
		() => {
			if (isReadOnly) {
				setSourceData({});
			} else {
				setSourceData({
					memberInfo: { ...initMemberState },
					mainSection: { ...initMainSectionState },
					invoice: {},
				});
			}
			initOptions();
		},
		// eslint-disable-next-line
		[isReadOnly],
	);

	const actionsData = useMemo(
		() => {
			if (isReadOnly)
				return [
					{
						label: "返回總覽",
						variant: "contained",
						as: Link,
						to: CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.GET_TABS_PATH("overview"),
					},
					{
						label: "變更信用卡",
						onClick: () => dialogRef?.current?.openSmartFitnessBandEditCreditCardDialog?.(sourceData),
					},
				];
			return [
				{
					label: "返回總覽",
					as: Link,
					to: CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.GET_TABS_PATH("overview"),
				},
			];
		},
		// eslint-disable-next-line
		[sourceData],
	);

	return (
		<SmartFitnessProvider value={SmartFitnessValue}>
			<PageLayout>
				<PageLayout.Header title={isReadOnly ? "智能系統訂閱單" : "智能系統申請書"} actions={actionsData}>
					{/*智能系統綁定LINE官方站台*/}
					{sourceData && sourceData?.memberID > 0 && <MemberLineBindingIconButton referenceID={sourceData?.memberID} />}
				</PageLayout.Header>
				{sourceData !== null ? isReadOnly ? <SmartFitnessBandContractDetail /> : <SmartFitnessBandContractApply /> : null}
				<SmartFitnessBandDialogBox ref={dialogRef} />
			</PageLayout>
		</SmartFitnessProvider>
	);
};

export default SmartFitnessBandContractPage;
